import { Conversation, Sender } from '@/types';
import { MessageCard } from '@/components/MessageCard';
import { CreateConversationField } from './CreateConversationField';

import { Button } from 'components/shadcn/button';
import { Separator } from 'components/shadcn/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/shadcn/tooltip';
import { Clock3, Hash, MessageSquare, Sparkles, X } from 'lucide-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'utils';

interface Props {
  conversation: Conversation | null;
  fieldNames: string[];
  onClose: () => void;
}

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

interface FieldEntry {
  name: string;
  value: unknown;
  inferred: boolean;
}

function FieldList({ entries }: { entries: FieldEntry[] }) {
  const { t } = useTranslation();

  if (!entries.length) return null;

  return (
    <dl className="divide-y border-y">
      {entries.map(({ name, value, inferred }) => (
        <div key={name} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.5fr)] gap-4 py-3">
          <dt className="flex min-w-0 items-start gap-1.5 wrap-break-word text-xs font-medium leading-5 text-muted-foreground">
            {name}
            {inferred && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Sparkles
                    aria-label={t('dashboard.conversations.sidebar.inferredByAi')}
                    className="mt-1 size-2.5 shrink-0 text-muted-foreground/70"
                  />
                </TooltipTrigger>
                <TooltipContent>{t('dashboard.conversations.sidebar.inferredByAi')}</TooltipContent>
              </Tooltip>
            )}
          </dt>
          <dd className="min-w-0 wrap-break-word text-sm font-medium leading-5 text-foreground">
            {formatValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ConversationFacts({
  conversation,
  fieldNames,
}: {
  conversation: Conversation;
  fieldNames: string[];
}) {
  const { t } = useTranslation();
  const lastMessage = conversation.messages[conversation.messages.length - 1];
  const lastMessageTime = lastMessage?.createdAt;
  const fieldEntries = useMemo(
    () =>
      fieldNames.map((name) => {
        const metadataValue = conversation.metadata?.[name];
        const extractedDataValue = conversation.extractedData?.[name];
        return {
          name,
          value: metadataValue || extractedDataValue,
          inferred: !metadataValue && extractedDataValue !== undefined,
        };
      }),
    [conversation.extractedData, conversation.metadata, fieldNames],
  );

  return (
    <div className="flex flex-col gap-6">
      <section aria-labelledby="conversation-activity-heading" className="flex flex-col gap-3">
        <h4
          id="conversation-activity-heading"
          className="text-sm font-semibold leading-5 text-foreground"
        >
          {t('dashboard.conversations.sidebar.activity')}
        </h4>
        <dl className="grid grid-cols-2 rounded-lg border bg-card">
          <div className="flex flex-col gap-1 p-4">
            <dt className="flex items-center gap-2 text-xs text-muted-foreground">
              <MessageSquare className="size-3.5" aria-hidden="true" />
              {t('dashboard.conversations.table.messages')}
            </dt>
            <dd className="text-sm font-medium leading-5 tabular-nums text-foreground">
              {conversation.messages.length}
            </dd>
          </div>
          <div className="flex flex-col gap-1 p-4">
            <dt className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock3 className="size-3.5" aria-hidden="true" />
              {t('dashboard.conversations.table.lastMessageTime')}
            </dt>
            <dd className="text-sm font-medium leading-5 tabular-nums text-foreground">
              {lastMessageTime ? (
                <time dir="ltr">{dayjs(lastMessageTime).format('DD/MM/YYYY HH:mm')}</time>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </dl>
      </section>

      <Separator />

      <section aria-labelledby="conversation-fields-heading" className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-2">
          <h4 id="conversation-fields-heading" className="text-sm font-semibold">
            {t('dashboard.conversations.sidebar.fields')}
          </h4>
          <CreateConversationField chatId={conversation.chatId} fieldNames={fieldNames} />
        </div>

        {fieldEntries.length > 0 && <FieldList entries={fieldEntries} />}
        {!fieldEntries.length && (
          <p className="py-2 text-sm text-muted-foreground">
            {t('dashboard.conversations.sidebar.noFields')}
          </p>
        )}
      </section>
    </div>
  );
}

export function ConversationSidebar({ conversation, fieldNames, onClose }: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('messages');

  useEffect(() => {
    setActiveTab('messages');
  }, [conversation?.chatId, conversation?.username]);

  return (
    <div
      className={cn(
        'fixed right-0 top-16 z-40 h-[calc(100vh-4rem)] shrink-0 overflow-hidden border-s bg-card shadow-xl transition-[width,opacity,box-shadow] duration-300 ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none md:sticky md:right-auto',
        conversation
          ? 'w-full opacity-100 md:w-112.5'
          : 'pointer-events-none w-0 border-s-0 opacity-0 shadow-none',
      )}
    >
      <div className="flex h-full w-full flex-col bg-background/50 backdrop-blur-md">
        {conversation && (
          <>
            <div className="flex items-center justify-between gap-4 border-b p-4">
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-lg font-semibold text-foreground">
                  {conversation.username}
                </h3>
                <p className="text-xs text-muted-foreground">{conversation.chatId}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0 rounded-full transition-transform duration-200 hover:bg-muted active:scale-[0.96]"
                onClick={onClose}
                aria-label={t('ui.close')}
              >
                <X size={18} />
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="messages" className="gap-2">
                    <MessageSquare className="h-4 w-4" aria-hidden="true" />
                    {t('dashboard.conversations.sidebar.messagesTab')}
                  </TabsTrigger>
                  <TabsTrigger value="fields" className="gap-2">
                    <Hash className="h-4 w-4" aria-hidden="true" />
                    {t('dashboard.conversations.sidebar.fieldsTab')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="messages">
                  <MessageList conversation={conversation} />
                </TabsContent>
                <TabsContent value="fields">
                  <ConversationFacts conversation={conversation} fieldNames={fieldNames} />
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function MessageList({ conversation }: { conversation: Conversation }) {
  return (
    <div className="space-y-4">
      {conversation.messages.length ? (
        conversation.messages.map((message, index) => (
          <div
            key={message.id || index}
            className="animate-in fill-mode-backwards slide-in-from-bottom-2 fade-in duration-300 motion-reduce:animate-none"
            style={{ animationDelay: `${Math.min(index * 50, 250)}ms` }}
          >
            <p className="text-sm font-medium capitalize text-foreground">
              {message.sender === Sender.User ? 'User' : message.sender.toLowerCase()}
            </p>
            <p className="mb-1 text-xs text-muted-foreground">
              {message.createdAt ? dayjs(message.createdAt).format('DD/MM/YYYY HH:mm:ss') : '—'}
            </p>
            <MessageCard showSuggestionList detailed message={message} />
          </div>
        ))
      ) : (
        <p className="text-sm text-muted-foreground">—</p>
      )}
    </div>
  );
}
